import {
  getAllPerpendicularBorders,
  getPlanareaSplitVector,
  getSplitLine,
  getAllParallelBorders,
  switchCursor,
} from '../helpers';
import { Coordinate } from './coordinate';
import { RenderMode } from './planarea';

export class AreaSplitter {
  splitLineColor = '#BE0000';
  splitLineWidth = 4;
  snapRadius = 10;

  constructor(options) {
    ({
      parentGroup: this.parentGroup,
      onPlanareaSplit: this.onPlanareaSplit,
      view: this.view,
      onViewChange: this.onViewChange,
    } = options);

    this.events = [];
  }

  render(renderMode, planarea) {
    this.angle = 0;
    if (renderMode === RenderMode.SplitPerpendicular) {
      this.angle = 0.5 * Math.PI;
    }
    this.group = this.parentGroup.createGroup();
    this.group.rawNode.style.pointerEvents = 'none';
    this.group.moveToFront();
    this.clear();

    this._calculateSnapSegments(planarea, this.angle);

    const moveEvent = this.parentGroup.connect('mousemove', e => {
      this.group.clear();
      switchCursor(this.parentGroup, 'pointer');
      const splitSegment = this._getGlobalSplitPoints(e, planarea);
      if (splitSegment) {
        const renderSegment = this._snapOrReturnCurrent(splitSegment);
        this._renderSplitLine(renderSegment);
        this.onViewChange(this._getMeasurementPoints(renderSegment, planarea));
      }
    });

    const clickEvent = this.parentGroup.connect('mousedown', e => {
      const splitSegment = this._getGlobalSplitPoints(e, planarea);
      if (splitSegment) {
        const renderSegment = this._snapOrReturnCurrent(splitSegment);
        this.onPlanareaSplit(renderSegment);
      }
    });

    const leaveEvent = this.parentGroup.connect('mouseleave', e => {
      this.group.clear();
    });

    this.events.push(moveEvent, clickEvent, leaveEvent);
  }

  clear() {
    this.events.forEach(token => token.remove());
  }

  _renderSplitLine(segment) {
    const localStartPoint = this.view.convertGlobalToLocal(segment.ps);
    const localEndPoint = this.view.convertGlobalToLocal(segment.pe);

    this.group
      .createLine({
        x1: localStartPoint.x,
        y1: localStartPoint.y,
        x2: localEndPoint.x,
        y2: localEndPoint.y,
      })
      .setStroke({ color: this.splitLineColor, width: this.splitLineWidth });
  }

  _getGlobalSplitPoints(event, planarea) {
    const currentPositionGlobal = this.view.convertPageToGlobal(new Coordinate(event.pageX, event.pageY));
    return getSplitLine(currentPositionGlobal.getFlattenPoint(), planarea, this.angle);
  }

  _snapOrReturnCurrent(current) {
    for (const segment of this.snapSegments) {
      if (segment.distanceTo(current)[0] < this.view.convertLocalToGlobalDistance(this.snapRadius)) {
        return segment;
      }
    }

    return current;
  }

  _calculateSnapSegments(planarea, angle) {
    this.snapSegments = [];

    const lineVector = getPlanareaSplitVector(planarea, angle);

    // snap to middle points of the perpendicular cutting line
    const perpendicullarBorders = getAllPerpendicularBorders(lineVector, planarea);
    perpendicullarBorders.forEach(border => {
      const segment = getSplitLine(border.getCenter().getFlattenPoint(), planarea, angle);
      if (segment) {
        this.snapSegments.push(segment);
      }
    });

    // snap to the inner lines parallel to the cutting line
    const parallelBorders = getAllParallelBorders(lineVector, planarea);
    parallelBorders.forEach(border => {
      const segment = getSplitLine(border.startPoint.getFlattenPoint(), planarea, angle);
      const segment2 = getSplitLine(border.endPoint.getFlattenPoint(), planarea, angle);

      if (segment) this.snapSegments.push(segment);
      if (segment2) this.snapSegments.push(segment2);
    });
  }

  _getMeasurementPoints(split, planarea) {
    return {
      points: [...planarea.getPolygonPoints()],
      additionalPoints: [
        (split => {
          return { x: Math.round(split.ps.x), y: Math.round(split.ps.y) };
        })(split),
        (split => {
          return { x: Math.round(split.pe.x), y: Math.round(split.pe.y) };
        })(split),
      ],
    };
  }
}
